import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, X, Send, Loader2, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { useUIStore, useAuthStore, useChatStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const VoiceOverlay = () => {
  const { voiceOverlayOpen, setVoiceOverlayOpen, language } = useUIStore();
  const { token } = useAuthStore();
  const { sendMessage } = useChatStore();
  const { t } = useTranslation(language);
  
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [error, setError] = useState('');
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const startRecording = async () => {
    try {
      setError('');
      setTranscript('');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        await transcribeAudio(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      setError(language === 'hi' ? 'माइक्रोफ़ोन एक्सेस अस्वीकृत' : 'Microphone access denied');
      console.error('Microphone error:', err);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const response = await fetch(`${API_URL}/voice/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Transcription failed');
      }

      const data = await response.json();
      setTranscript(data.text);
    } catch (err) {
      setError(language === 'hi' ? 'ट्रांसक्रिप्शन विफल' : 'Failed to transcribe audio');
      console.error('Transcription error:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendToChat = async () => {
    if (!transcript.trim()) return;
    
    setIsProcessing(true);
    try {
      await sendMessage(transcript);
      setTranscript('');
      setVoiceOverlayOpen(false);
    } catch (err) {
      setError(language === 'hi' ? 'संदेश भेजने में विफल' : 'Failed to send message');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    if (isRecording) {
      stopRecording();
    }
    setTranscript('');
    setError('');
    setVoiceOverlayOpen(false);
  };

  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
      }
    };
  }, [isRecording]);

  const getStatusText = () => {
    if (isRecording) {
      return language === 'hi' ? '🎙️ बोलें... रिकॉर्डिंग जारी है' : '🎙️ Speak now... Recording';
    }
    if (isProcessing) {
      return language === 'hi' ? '⏳ प्रोसेसिंग...' : '⏳ Processing...';
    }
    if (transcript) {
      return language === 'hi' ? '✅ AI चैट में भेजने के लिए तैयार' : '✅ Ready to send to AI Chat';
    }
    return language === 'hi' ? '🎤 रिकॉर्ड करने के लिए माइक दबाएं' : '🎤 Press mic to start recording';
  };

  return (
    <AnimatePresence>
      {voiceOverlayOpen && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50"
          data-testid="voice-overlay"
        >
          <div className="glass rounded-2xl p-6 shadow-2xl min-w-[400px] max-w-[500px]">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                <span className="font-medium">
                  {language === 'hi' ? 'वॉयस से AI चैट' : 'Voice to AI Chat'}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                className="h-8 w-8 hover:bg-white/10 rounded-full"
                data-testid="voice-close-btn"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Status */}
            <p className="text-sm text-muted-foreground mb-4 text-center">
              {getStatusText()}
            </p>

            {/* Transcript Display */}
            {transcript && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mb-4 p-3 rounded-xl bg-secondary/50 border border-white/10"
              >
                <p className="text-xs text-muted-foreground mb-1">
                  {language === 'hi' ? 'आपने कहा:' : 'You said:'}
                </p>
                <p className="text-sm">{transcript}</p>
              </motion.div>
            )}

            {/* Waveform visualization */}
            <div className="flex items-center justify-center gap-2 h-16 mb-4">
              {isRecording ? (
                Array.from({ length: 7 }).map((_, i) => (
                  <motion.div
                    key={i}
                    className="w-1.5 bg-primary rounded-full"
                    animate={{
                      height: [16, 40, 16],
                    }}
                    transition={{
                      duration: 0.6,
                      repeat: Infinity,
                      delay: i * 0.08,
                    }}
                  />
                ))
              ) : (
                <div className="flex items-center gap-1">
                  {Array.from({ length: 7 }).map((_, i) => (
                    <div
                      key={i}
                      className="w-1.5 h-4 bg-muted-foreground/30 rounded-full"
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-center gap-4">
              {/* Record/Stop Button */}
              <Button
                variant={isRecording ? 'destructive' : 'default'}
                size="lg"
                onClick={isRecording ? stopRecording : startRecording}
                disabled={isProcessing}
                className={`rounded-full h-16 w-16 ${isRecording ? 'animate-pulse' : 'glow-primary'}`}
                data-testid="voice-record-btn"
              >
                {isProcessing ? (
                  <Loader2 className="h-7 w-7 animate-spin" />
                ) : isRecording ? (
                  <MicOff className="h-7 w-7" />
                ) : (
                  <Mic className="h-7 w-7" />
                )}
              </Button>

              {/* Send to Chat Button */}
              {transcript && !isRecording && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                >
                  <Button
                    size="lg"
                    onClick={handleSendToChat}
                    disabled={isProcessing}
                    className="rounded-full h-16 px-6 glow-primary"
                    data-testid="voice-send-btn"
                  >
                    <Send className="h-5 w-5 mr-2" />
                    {language === 'hi' ? 'AI चैट में भेजें' : 'Send to AI Chat'}
                  </Button>
                </motion.div>
              )}
            </div>

            {/* Error message */}
            {error && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-destructive text-sm text-center mt-4"
              >
                {error}
              </motion.p>
            )}

            {/* Help text */}
            <p className="text-xs text-muted-foreground text-center mt-4">
              {language === 'hi' 
                ? 'उदाहरण: "एक नया प्रोजेक्ट बनाओ" या "मेरे टास्क दिखाओ"'
                : 'Try: "Create a new project" or "Show my tasks"'}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Floating mic button (always visible when logged in)
export const VoiceMicButton = () => {
  const { setVoiceOverlayOpen, voiceOverlayOpen, language } = useUIStore();
  const { isAuthenticated } = useAuthStore();

  if (!isAuthenticated || voiceOverlayOpen) return null;

  return (
    <motion.button
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => setVoiceOverlayOpen(true)}
      className="fixed bottom-8 right-8 z-40 h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-lg glow-primary flex items-center justify-center group"
      data-testid="voice-mic-floating-btn"
      aria-label="Open voice input"
    >
      <Mic className="h-6 w-6" />
      <span className="absolute right-full mr-3 px-3 py-1.5 rounded-lg bg-card text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity border border-white/10">
        {language === 'hi' ? 'वॉयस इनपुट' : 'Voice Input'}
      </span>
    </motion.button>
  );
};

export default VoiceOverlay;
